/**
 * /policies/shifts/rotations — M3X.ATTENDANCE F.3.
 *
 * Per-LE rotation pattern catalog. Lists existing patterns + shows
 * cycle length + a tester input (position_id + on_date) that calls
 * the resolver and surfaces the resolved shift id.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "../components/ui";
import { api } from "../lib/api";

interface Pattern {
  id: string;
  legal_entity_id: string;
  code: string;
  name_en: string;
  name_ar: string | null;
  description: string | null;
  steps: Array<{ shift_id: string; day_count: number }>;
  cycle_length_days: number;
}

export function ShiftRotationPatternsPage() {
  const { t } = useTranslation();
  const [patterns, setPatterns] = useState<Pattern[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [posId, setPosId] = useState("");
  const [onDate, setOnDate] = useState(new Date().toISOString().slice(0, 10));
  const [resolved, setResolved] = useState<string | null>(null);

  useEffect(() => {
    api
      .listShiftRotationPatterns()
      .then(setPatterns)
      .catch((e) => setErr(String(e)));
  }, []);

  async function testResolve() {
    setResolved(null);
    setErr(null);
    try {
      const r = await api.resolveShiftForDate(posId, onDate);
      setResolved(r.shift_id ?? "(none — no active rotation)");
    } catch (e) {
      setErr(String(e));
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("shift_rotation.title", "Shift Rotation Patterns")}
        subtitle={t(
          "shift_rotation.subtitle",
          "F.3 — auto-rotating shifts. Pattern walks its cycle from effective_from.",
        )}
      />

      {/* Pattern list */}
      <div className="rounded-lg border border-slate-200 bg-surface-default p-5">
        <h3 className="mb-3 text-base font-semibold">
          {t("shift_rotation.patterns_list", "Registered patterns")}
        </h3>
        {patterns.length === 0 ? (
          <p className="text-sm text-text-tertiary">
            {t(
              "shift_rotation.empty",
              "No rotation patterns yet. Create one via POST /api/shift-rotation/patterns or the Add modal (coming soon).",
            )}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-border-subtle text-xs uppercase text-text-secondary">
              <tr>
                <th className="px-2 py-1 text-start">Code</th>
                <th className="px-2 py-1 text-start">Name</th>
                <th className="px-2 py-1 text-end">Cycle days</th>
                <th className="px-2 py-1 text-end">Steps</th>
              </tr>
            </thead>
            <tbody>
              {patterns.map((p) => (
                <tr key={p.id} className="border-b border-border-subtle">
                  <td className="px-2 py-1 font-mono text-xs">{p.code}</td>
                  <td className="px-2 py-1">{p.name_en}</td>
                  <td className="px-2 py-1 text-end font-mono">
                    {p.cycle_length_days}
                  </td>
                  <td className="px-2 py-1 text-end font-mono">
                    {p.steps.length}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Tester */}
      <div className="rounded-lg border border-slate-200 bg-surface-default p-5">
        <h3 className="mb-3 text-base font-semibold">
          {t("shift_rotation.tester", "Resolver tester")}
        </h3>
        <p className="mb-3 text-xs text-text-tertiary">
          {t(
            "shift_rotation.tester_help",
            "Enter a Position id + a date. The resolver walks the active rotation cycle and returns the bound shift.",
          )}
        </p>
        <div className="flex flex-wrap gap-2 text-sm">
          <input
            type="text"
            placeholder="position_id (uuid)"
            value={posId}
            onChange={(e) => setPosId(e.target.value)}
            className="flex-1 rounded-md border border-border-default p-2 font-mono text-xs"
          />
          <input
            type="date"
            value={onDate}
            onChange={(e) => setOnDate(e.target.value)}
            className="rounded-md border border-border-default p-2"
          />
          <button
            type="button"
            onClick={testResolve}
            disabled={!posId}
            className="rounded-md bg-brand-blue px-3 py-2 text-white disabled:opacity-50"
          >
            {t("shift_rotation.resolve", "Resolve")}
          </button>
        </div>
        {resolved && (
          <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 p-3 text-xs font-mono text-emerald-900">
            shift_id = {resolved}
          </div>
        )}
      </div>

      {err && (
        <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
          ✦ {err}
        </div>
      )}
    </div>
  );
}

export default ShiftRotationPatternsPage;
